import { gql } from 'graphql-tag'
import { timePeriodFragment } from '../scoreCard/ScoreCardQueries'
import { hesburgerEfficiencyBoundaryFragment } from '../station/stationQueries'

const generalFragment = gql`
  fragment MonthlyInfoGeneralTypeFragment on MonthlyInfoGeneralType {
    id
    profitCenterType
    totalBudgetedSales
    totalBudgetedHours
    totalBudgetedSalesTaxfree
    totalPlannedHours
    totalPlannedSales
    plannedEfficiency
    budgetedEfficiency
    budgetedEfficiencyTaxfree
    totalActualSales
    totalActualHours
    totalActualSalesTaxfree
    isActionRequired
  }
`

const restaurantFragment = gql`
  fragment MonthlyInfoRestaurantTypeFragment on MonthlyInfoRestaurantType {
    id
    profitCenterType
    salesIncentiveTarget
    totalActualSalesIncentive
    isActionRequired
    currentMonthSalesIncentiveTarget
    currentMonthTotalActualSalesIncentive
  }
`

const kitchenFragment = gql`
  fragment MonthlyInfoKitchenTypeFragment on MonthlyInfoKitchenType {
    id
    profitCenterType
    productGroupName
    productGroupId
    totalWastageAmount
    totalWastageVolume
    totalSalesAmount
    isActionRequired
    totalSalesVolume
  }
`

const buffetWastageFragment = gql`
  fragment MonthlyInfoBuffetWastageFragment on MonthlyInfoBuffetWastageType {
    id
    buffetWastages {
      date
      isAvailable
    }
    isActionRequired
  }
`

const customerPathFragment = gql`
  fragment MonthlyInfoCustomerPathFragment on MonthlyInfoCustomerPathType {
    id
    description
    isActionRequired
  }
`

const marketFragment = gql`
  fragment MonthlyInfoMarketTypeFragment on MonthlyInfoMarketType {
    id
    description
    isActionRequired
  }
`

const scoreCardFragment = gql`
  fragment MonthlyInfoScoreCardFragment on MonthlyInfoScoreCardType {
    id
    isActionRequired
  }
`

const hesburgerEfficiencyFactorFragment = gql`
  fragment HesburgerEfficiencyFactorFragment on HesburgerEfficiencyFactor {
    date
    ipShare
    avgPurchase
    salesMargin
    productWastage
    raWastage
    inventoryDifferences
    intakes
    readinessCheeseBurger
    readinessDoubleBurger
    readinessCampaign
    isActionRequired
  }
`

export const fetchMonthlyInfo = gql`
  query FetchMonthlyInfo($stationId: ID!, $date: String!, $type: MonthlyInfoEnumType!) {
    monthlyInfo(stationId: $stationId, date: $date, type: $type) {
      ...MonthlyInfoGeneralTypeFragment
      ...MonthlyInfoRestaurantTypeFragment
      ...MonthlyInfoKitchenTypeFragment
      ...MonthlyInfoMarketTypeFragment
      ...MonthlyInfoCustomerPathFragment
      ...MonthlyInfoBuffetWastageFragment
    }
    wastageBoundaries: getAllWastageBoundariesForAllProductTypes(stationId: $stationId) {
      productType
      lowerWastageBounds
      upperWastageBounds
    }
  }

  ${generalFragment}
  ${restaurantFragment}
  ${customerPathFragment}
  ${kitchenFragment}
  ${buffetWastageFragment}
  ${marketFragment}
`

export const fetchMonthlyInfoScoreCard = gql`
  query FetchMonthlyInfoScoreCard($stationId: ID!, $date: String!, $type: MonthlyInfoEnumType!, $row: String!) {
    monthlyInfo: monthlyInfoScoreCard(stationId: $stationId, date: $date, type: $type, row: $row) {
      ...MonthlyInfoScoreCardFragment
    }
  }

  ${scoreCardFragment}
`

export const upsertMonthlyInfo = gql`
  mutation UpsertMonthlyInfo($stationId: ID!, $date: String!, $monthlyInfo: UpdateMonthlyInfoInput!) {
    upsertMonthlyInfo(stationId: $stationId, date: $date, monthlyInfo: $monthlyInfo)
  }
`

export const fetchMonthlyInfoNotes = gql`
  query FetchMonthlyInfoNotes($stationId: ID!, $date: String!, $type: MonthlyInfoEnumType!) {
    monthlyInfoNotes(stationId: $stationId, date: $date, type: $type) {
      id
      body
    }
  }
`

export const createNote = gql`
  mutation CreateNewNote($stationId: ID!, $date: String!, $type: MonthlyInfoEnumType!, $body: String!) {
    createMonthlyInfoNotes(stationId: $stationId, date: $date, type: $type, body: $body) {
      id
      body
    }
  }
`
export const updateNote = gql`
  mutation UpdateNote($noteId: ID!, $updatedBody: String) {
    updateMonthlyInfoNote(noteId: $noteId, updatedBody: $updatedBody) {
      id
      body
    }
  }
`
export const deleteNote = gql`
  mutation DeleteNote($noteId: ID!) {
    deleteMonthlyInfoNotes(noteId: $noteId)
  }
`

export const fetchHesburgerEfficiencies = gql`
  query FetchHesburgerEfficiencies($stationId: ID!, $date: String!) {
    hesburgerEfficiencyFactor(stationId: $stationId, date: $date) {
      ...HesburgerEfficiencyFactorFragment
    }
    hesburgerEfficiencyBoundary(stationId: $stationId) {
      ...HesburgerEfficiencyBoundaryFragment
    }
  }

  ${hesburgerEfficiencyFactorFragment}
  ${hesburgerEfficiencyBoundaryFragment}
`

export const saveHesburgerEfficiencyFactor = gql`
  mutation SaveHesburgerEfficiencyFactor(
    $stationId: ID!
    $date: String!
    $data: UpsertHesburgerEfficiencyFactorInput!
  ) {
    upsertHesburgerEfficiencyFactor(stationId: $stationId, date: $date, data: $data) {
      ...HesburgerEfficiencyFactorFragment
    }
  }

  ${hesburgerEfficiencyFactorFragment}
`

export const fetchRestaurantPageScoreCardStats = gql`
  query FetchStationScoreCardStats($stationId: ID!) {
    scoreCardStats: newScoreCardStationStats(stationId: $stationId) {
      toiletChecks {
        ...ScoreCardTimePeriodFragment
      }
      dailyChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
      continuingChecklistUsageMonthly {
        ...ScoreCardTimePeriodFragment
      }
      continuingChecklistUsageWeekly {
        ...ScoreCardTimePeriodFragment
      }
      continuingChecklistUsageDaily {
        ...ScoreCardTimePeriodFragment
      }
      hourlyHallChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
      hourlyBuffetChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
      hourlyVitrineChecklistUsage {
        ...ScoreCardTimePeriodFragment
      }
    }
  }
  ${timePeriodFragment}
`

export const fetchKitchenPageScoreCardStats = gql`
  query FetchStationScoreCardStats($stationId: ID!, $date: String) {
    scoreCardStats: newScoreCardStationStats(stationId: $stationId, date: $date) {
      dailyInfoUsage {
        ...ScoreCardTimePeriodFragment
      }
    }
  }
  ${timePeriodFragment}
`

export const fetchManagerPageScoreCardStats = gql`
  query FetchStationScoreCardStats($stationId: ID!) {
    scoreCardStats: newScoreCardStationStats(stationId: $stationId) {
      budgetEfficiency {
        ...ScoreCardTimePeriodFragment
      }
      targetEfficiency {
        ...ScoreCardTimePeriodFragment
      }
    }
  }
  ${timePeriodFragment}
`

export const fetchMonthlyInfoMarketSalesAndProfit = gql`
  query FetchMonthlyInfoMarketSalesAndProfit($stationId: ID!, $date: String!) {
    monthlyInfoMarketSalesAndProfit(stationId: $stationId, date: $date) {
      id
      cdtId
      cdtName
      salesAmountTaxfree
      salesVolume
      salesAmountTaxfreeLastYear
      salesVolumeLastYear
      isActionRequired
      childVolumesAndSums {
        id
        cdtId
        cdtName
        salesAmountTaxfree
        salesVolume
        salesAmountTaxfreeLastYear
        salesVolumeLastYear
        isActionRequired
      }
    }

    chainMarketSettings {
      id
      name
      startDate
      endDate
      value
    }
  }
`

export const fetchMonthlyInfoMarketWastages = gql`
  query FetchMonthlyInfoMarketWastages($stationId: ID!, $date: String!) {
    monthlyInfoMarketWastages(stationId: $stationId, date: $date) {
      id
      wastageAmountTaxfree
      wastageAmountTaxfreePreviousMonth
      wastagePercentage
      wastagePercentagePreviousMonth
      salesAmountTaxfree
      cdtId
      cdtName
      isActionRequired
      wastageLowerBound
      wastageUpperBound
      children {
        id
        cdtId
        cdtName
        wastageAmountTaxfree
        wastageAmountTaxfreePreviousMonth
        wastagePercentage
        wastagePercentagePreviousMonth
        salesAmountTaxfree
        isActionRequired
        wastageLowerBound
        wastageUpperBound
        children {
          id
          cdtId
          cdtName
          wastageAmountTaxfree
          wastageAmountTaxfreePreviousMonth
          wastagePercentage
          wastagePercentagePreviousMonth
          salesAmountTaxfree
          isActionRequired
          wastageLowerBound
          wastageUpperBound
        }
      }
    }
  }
`
